import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useEffect, useState } from 'react';

export type VirtualizedGridProps<T> = {
  items: T[];
  getKey: (item: T, index: number) => string;
  columnCount?: number;
  /** Estimated row height in px (including gaps). */
  estimatedRowHeight?: number;
  scrollElement: Element | null;
  renderItem: (item: T, index: number) => React.ReactNode;
  rowClassName?: string;
  /** When true, rows are measured after mount for accurate scroll positions. */
  measureRows?: boolean;
  /** Extra rows rendered above/below the viewport. Keep low for image-heavy lists. */
  overscan?: number;
  /** Vertical gap between virtual rows (px). Included in scroll size by the virtualizer. */
  rowGap?: number;
  emptyMessage?: React.ReactNode;
  /** Scroll so this item index is visible (e.g. active template when reopening a tab). */
  scrollToItemIndex?: number | null;
};

/**
 * Virtualized grid/list for long thumbnail collections. Uses @tanstack/react-virtual
 * because it works with existing scroll containers (nested sidebars) and flex-wrap grids.
 */
function VirtualizedGrid<T>({
  items,
  getKey,
  columnCount = 2,
  estimatedRowHeight = 130,
  scrollElement,
  renderItem,
  rowClassName = 'virtualized-grid-row',
  measureRows = true,
  overscan = 4,
  rowGap = 0,
  emptyMessage = null,
  scrollToItemIndex = null,
}: VirtualizedGridProps<T>) {
  const rowCount = Math.ceil(items.length / columnCount) || 0;
  const [scrollReady, setScrollReady] = useState(Boolean(scrollElement));

  useEffect(() => {
    setScrollReady(Boolean(scrollElement));
  }, [scrollElement]);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimatedRowHeight,
    overscan,
    gap: rowGap,
  });

  useEffect(() => {
    if (measureRows) {
      virtualizer.measure();
    }
  }, [measureRows, estimatedRowHeight, columnCount, rowCount, virtualizer]);

  useEffect(() => {
    if (
      scrollToItemIndex == null
      || scrollToItemIndex < 0
      || !scrollReady
      || items.length === 0
    ) {
      return;
    }
    const rowIndex = Math.floor(scrollToItemIndex / columnCount);
    virtualizer.scrollToIndex(rowIndex, { align: 'center' });
  }, [scrollToItemIndex, scrollReady, columnCount, items.length, virtualizer]);

  if (items.length === 0) {
    return emptyMessage ? <>{emptyMessage}</> : null;
  }

  if (!scrollReady) {
    return null;
  }

  return (
    <div
      className="virtualized-grid"
      style={{
        height: virtualizer.getTotalSize(),
        position: 'relative',
        width: '100%',
      }}
    >
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const startIndex = virtualRow.index * columnCount;
        const rowItems = items.slice(startIndex, startIndex + columnCount);

        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={measureRows ? virtualizer.measureElement : undefined}
            className={rowClassName}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'flex-start',
            }}
          >
            {rowItems.map((item, offset) => {
              const index = startIndex + offset;
              return (
                <React.Fragment key={getKey(item, index)}>
                  {renderItem(item, index)}
                </React.Fragment>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default VirtualizedGrid;
